import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NewPaymentContext } from '../payment.context';
import {
  selectIsLoggedIn,
  selectPaymentInstrumentById,
} from 'redux/user/user.selectors';
import {
  useMakePayment,
  useMakePaymentBody,
  usePaymentGiftCardAsAGuest,
  usePaymentMode,
  usePurchaseEmailGiftCardBody,
} from '../payment.new.hooks';
import INC_BASE_API from 'apis/incentivio-api';
import { IncentivioApiError } from 'util/errors';
import { useCheckoutPaymentLoading } from 'pages/checkout/checkout.component';
import {
  selectIsFreeOrder,
  selectPaymentInstrumentId,
  selectPaymentOption,
} from 'redux/cart/cart.selectors';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { PaymentOptionType } from '../payment.constants';
import {
  useSquareCardTokenize,
  useSquareTokenizeStoredCard,
  useSquareVerificationDetails,
} from './square-card/square-card.hooks';
import { useErrorHandler } from 'react-error-boundary';
import getSquare from 'util/getSquare';

export const usePlaceOrderWithCard = () => useMakePayment(useMakePaymentBody());

export const useSquarePayments = () => {
  const [squareLoaded, setSquareLoaded] = useState({});
  const [squareCard, setSquareCard] = useState(null);

  const handleError = useErrorHandler();

  useEffect(() => {
    const execute = async () => {
      try {
        await new Promise(async (resolve, reject) => {
          if (!!getSquare()) {
            setSquareLoaded(true);
            resolve();
            return;
          }
          setTimeout(async () => {
            // wait 1 second to see if Square is defined on the window. I
            // think this is only an issue when testing
            if (!getSquare()) {
              reject(new Error('window.Square is not defined'));
            }
            setSquareLoaded(true);
            resolve();
          }, 1000);
        });
      } catch (err) {
        handleError(err);
      }
    };
    execute();
  }, [handleError]);

  return {
    squareLoaded,
    setSquareCard,
    squareCard,
  };
};

export const useSquarePaymentsPlaceOrder = () => {
  const addPromise = useCheckoutPaymentLoading(state => state.addPromise);
  const paymentMode = usePaymentMode();
  const paymentOption = useSelector(selectPaymentOption);
  const history = useHistory();

  const { trigger: cardTrigger } = usePlaceOrderWithCard();
  const { trigger: lookupGiftCardTrigger } = usePaymentGiftCardAsAGuest();
  const tokenize = useSquareCardTokenize();
  const tokenizeStoredCard = useSquareTokenizeStoredCard();
  const buildVerificationDetails = useSquareVerificationDetails();

  const isFreeOrder = useSelector(selectIsFreeOrder);
  const paymentInstrumentId = useSelector(selectPaymentInstrumentId);
  const paymentInstrumentSelector = useCallback(
    state => selectPaymentInstrumentById(paymentInstrumentId)(state),
    [paymentInstrumentId],
  );
  const paymentInstrument = useSelector(paymentInstrumentSelector);

  let response;

  const trigger = async () => {
    try {
      if (paymentMode === 'NO_PAYMENT') {
        response = await addPromise(cardTrigger());
      } else {
        // eslint-disable-next-line default-case
        switch (paymentOption) {
          case PaymentOptionType.CARD: {
            // Nothing is charged on a free order, so there is no amount to bind
            // a token to and nothing for the issuer to verify.
            if (isFreeOrder) {
              response = await addPromise(cardTrigger());
              break;
            }

            // The stored ccof: id carries no verification, so it can't satisfy
            // SCA on its own. Re-tokenize it - what comes back is a fresh
            // single-use token, and that is what the backend charges.
            const storedCardToken = await tokenizeStoredCard(
              buildVerificationDetails({ intent: 'CHARGE' }),
              paymentInstrument?.paymentToken,
            );
            if (!storedCardToken) return;
            response = await addPromise(
              cardTrigger({ paymentToken: storedCardToken }),
            );
            break;
          }

          case PaymentOptionType.PAY_LATER:
          case PaymentOptionType.GIFT_CARD:
            response = await addPromise(cardTrigger());
            break;

          case PaymentOptionType.GUEST_CARD:
            const paymentToken = await tokenize(
              buildVerificationDetails({ intent: 'CHARGE' }),
            );
            if (!paymentToken) return;
            response = await addPromise(
              cardTrigger({
                paymentToken,
                isOneTimeTransaction: true,
                month: 0,
                year: 0,
              }),
            );
            break;

          case PaymentOptionType.GUEST_GIFT_CARD:
            const { lookupToken, giftCardNumber } = await addPromise(
              lookupGiftCardTrigger(),
            );
            if (lookupToken) {
              response = await cardTrigger({
                paymentToken: giftCardNumber,
                isOneTimeTransaction: true,
              });
            }
            break;
        }
      }
      history.push('/checkout/thankyou', response.data);
    } catch (e) {
      // handle errors in caller
      throw e;
    }
  };

  return { trigger };
};

export const useSquareEmailGiftCardPayment = () => {
  const [loading, setLoading] = useState(false);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const body = usePurchaseEmailGiftCardBody();
  const { paymentOption } = useContext(NewPaymentContext) ?? {};

  const tokenize = useSquareCardTokenize();
  const tokenizeStoredCard = useSquareTokenizeStoredCard();
  const buildVerificationDetails = useSquareVerificationDetails();

  const trigger = async paymentToken => {
    try {
      setLoading(true);

      // This charges the gift card's value rather than an order total, so the
      // token has to be bound to that amount.
      const verificationDetails = buildVerificationDetails({
        intent: 'CHARGE',
        amountInMilliunits: body.amount,
      });

      if (!isLoggedIn) {
        const token = await tokenize(verificationDetails);
        if (!token) {
          setLoading(false);
          return;
        }
        body.paymentToken = token;
      } else if (paymentOption === PaymentOptionType.CARD) {
        const token = await tokenizeStoredCard(
          verificationDetails,
          paymentToken,
        );
        if (!token) {
          setLoading(false);
          return;
        }
        body.paymentToken = token;
      } else {
        // Paying from a gift card balance or a wallet - no card to verify.
        body.paymentToken = paymentToken;
      }

      const response = await INC_BASE_API.post(`/giftcard/purchase`, body, {
        authenticated: isLoggedIn,
      });

      return response;
    } catch (e) {
      throw new IncentivioApiError('useSquareEmailGiftCardPayment error', e);
    } finally {
      setLoading(false);
    }
  };

  return { loading, trigger };
};

export const useSquarePurchaseEmailGiftCard = () => {
  const {
    loading: emailGiftCardLoading,
    trigger: triggerEmailGiftCardPayment,
  } = useSquareEmailGiftCardPayment();

  const loading = useMemo(() => emailGiftCardLoading, [emailGiftCardLoading]);

  const trigger = async paymentToken => {
    const resp = await triggerEmailGiftCardPayment(paymentToken);

    return resp;
  };

  return { loading, trigger };
};
